import React from 'react'
import { LayoutProps } from 'src/layouts/types'
import useResponsive from 'src/hooks/useResponsive'
import ScrollToTop from 'src/components/ui/scroll-to-top'

import Navigation from './components/vertical/navigation'
import GlobalSearchComponent from './components/GlobalSearchComponent'

import Fab from '@mui/material/Fab'
import Stack from '@mui/material/Stack'
import Icon from 'src/components/ui/icon'
import { styled } from '@mui/material/styles'
import { useSearchQuery } from 'src/hooks/useSearchQuery'
import { SearchAsyncModal } from 'src/modals/SearchAsyncModal'
import { useAccountSelect } from 'src/hooks/useAccount'
import {
  AccountsType,
  useSelectedAccounts,
} from 'src/hooks/useSelectedAccounts'
import { AssinstantWindow } from 'src/components/widgets/Assistant'
import { useAssistantSelect } from 'src/hooks/useAssistant'
import { drawerWidth, LayoutContext } from 'src/layouts/layoutContext'
import { useTwilioNewActions } from 'src/hooks/useTwilio'

const Main = styled('main', {
  shouldForwardProp: (prop) =>
    !['open', 'isMobile', 'modalOpened'].includes(String(prop)),
})<{
  open?: boolean
  isMobile: boolean
  modalOpened?: boolean
}>(({ theme, open, modalOpened, isMobile }) => ({
  width: '100vw',
  height: '100vh',

  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
  // Tokens (--app-*) are defined on :root in src/theme/globalStyles.ts
  backgroundColor: 'var(--app-page-bg)',
  backgroundImage: 'var(--app-page-bg-image)',
  backgroundBlendMode: 'var(--app-page-bg-blend)',
  backgroundSize: 'var(--app-page-bg-size)',

  '& .MuiPaper-root:not(.MuiDrawer-paper):not(.MuiAccordion-root)': {
    backgroundColor: 'var(--app-widget-surface)',
    backgroundImage: 'none',
    border: '1px solid var(--app-glass-border)',
    borderRadius: 'var(--app-widget-radius)',
    boxShadow: 'var(--app-widget-shadow), var(--app-glass-inner-border)',
    overflow: 'hidden',
  },
  '& .app-glass-surface': {
    backgroundColor: 'var(--app-glass-surface-strong)',
    backdropFilter: 'var(--app-glass-blur)',
    WebkitBackdropFilter: 'var(--app-glass-blur)',
  },
  '& .MuiTableContainer-root, & .MuiDataGrid-root': {
    backgroundColor: 'var(--app-widget-surface)',
    backgroundImage: 'none',
    borderColor: 'var(--app-glass-border)',
    borderRadius: 'var(--app-widget-radius)',
    boxShadow: 'var(--app-widget-shadow), var(--app-glass-inner-border)',
  },
  '& [data-object-page="true"] .MuiAccordion-root, & [data-object-page="true"] .MuiAccordion-root.Mui-expanded':
    {
      backgroundColor: 'var(--app-widget-surface)',
      backgroundImage: 'none',
      border: '1px solid var(--app-glass-border)',
      borderRadius: 'var(--app-widget-radius)',
      boxShadow: 'var(--app-widget-shadow), var(--app-glass-inner-border)',
      overflow: 'hidden',
    },
  '& [data-object-page="true"] .MuiAccordionSummary-root, & [data-object-page="true"] .MuiAccordionSummary-root.Mui-expanded':
    {
      background: 'var(--app-widget-header-surface)',
      boxShadow: 'none',
      borderBottom: '1px solid var(--app-border-faint)',
      transition:
        'background-color 160ms ease, background 160ms ease, border-color 160ms ease',
    },
  '& [data-object-page="true"] .MuiAccordionSummary-root:hover': {
    background: 'var(--app-widget-header-hover)',
    boxShadow: 'none',
  },
  '& [data-object-page="true"] .MuiAccordionSummary-root.Mui-expanded:hover': {
    background: 'var(--app-widget-header-hover)',
    boxShadow: 'none',
  },
  '& [data-object-page="true"] .MuiAccordionDetails-root': {
    background: 'transparent',
  },
  '& [data-object-page="true"] .object-page-widget.MuiAccordion-root, & [data-object-page="true"] .object-page-widget.MuiAccordion-root.Mui-expanded':
    {
      position: 'relative',
      flexShrink: 0,
      overflow: 'hidden',
      backgroundColor: 'var(--app-widget-content-surface)',
      backgroundImage: 'none',
      backdropFilter: 'none',
      WebkitBackdropFilter: 'none',
      border: '1px solid var(--app-glass-border)',
      borderRadius: 'var(--app-widget-radius)',
      boxShadow: 'var(--app-widget-shadow), var(--app-glass-inner-border)',
    },
  '& [data-object-page="true"] .object-page-widget.MuiAccordion-root::before': {
    display: 'none',
  },
  '& [data-object-page="true"] .object-page-widget-summary.MuiAccordionSummary-root, & [data-object-page="true"] .object-page-widget-summary.MuiAccordionSummary-root.Mui-expanded':
    {
      minHeight: { xs: 44, md: 52 },
      background: 'var(--app-widget-header-glass)',
      backdropFilter: 'var(--app-scroll-sheet-blur)',
      WebkitBackdropFilter: 'var(--app-scroll-sheet-blur)',
      borderBottom: '1px solid var(--app-border-faint)',
      boxShadow: 'var(--app-widget-header-shadow)',
      transition:
        'background-color 160ms ease, background 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
    },
  '& [data-object-page="true"] .object-page-widget-summary.Mui-disabled': {
    opacity: 1,
  },
  '& [data-object-page="true"] .object-page-widget-summary .MuiAccordionSummary-content, & [data-object-page="true"] .object-page-widget-summary.Mui-expanded .MuiAccordionSummary-content':
    {
      margin: { xs: '8px 0', md: '10px 0' },
      alignItems: 'center',
    },
  '& [data-object-page="true"] .object-page-widget-summary:hover': {
    background: 'var(--app-widget-header-glass-hover)',
  },
  '& [data-object-page="true"] .object-page-widget-summary .MuiAccordionSummary-expandIconWrapper':
    {
      color: 'text.secondary',
    },
  '& [data-object-page="true"] .object-page-widget-details.MuiAccordionDetails-root':
    {
      position: 'relative',
      background: 'var(--app-widget-content-surface)',
      backgroundImage: 'none',
    },
  '& [data-object-page="true"] .object-page-widget-details .MuiTableContainer-root':
    {
      backgroundColor: 'transparent',
      backgroundImage: 'none',
      border: 0,
      borderRadius: 0,
      boxShadow: 'none',
      backdropFilter: 'none',
      WebkitBackdropFilter: 'none',
    },
  '& [data-object-page="true"] .object-widget-glass-header, & [data-object-page="true"] .object-widget-glass-subheader':
    {
      backdropFilter: 'var(--app-scroll-sheet-blur)',
      WebkitBackdropFilter: 'var(--app-scroll-sheet-blur)',
      borderColor: 'var(--app-glass-border)',
    },
  '& [data-object-page="true"] .object-widget-glass-header': {
    background: 'var(--app-widget-header-glass)',
    zIndex: 50,
    boxShadow: 'var(--app-widget-header-shadow)',
  },
  '& [data-object-page="true"] .object-widget-glass-subheader': {
    background: 'var(--app-widget-subheader-glass)',
    zIndex: 40,
    boxShadow: 'var(--app-widget-subheader-shadow)',
  },
  '& [data-object-page="true"] .object-widget-scroll-frame': {
    position: 'relative',
    minHeight: 0,
    overflow: 'hidden',
    background: 'transparent',
  },
  '& [data-object-page="true"] .object-widget-scroll': {
    position: 'relative',
  },
  '& [data-object-page="true"] .object-widget-scroll--table .MuiTableHead-root':
    {
      position: 'sticky',
      top: 0,
      zIndex: 10,
    },
  '& [data-object-page="true"] .object-widget-scroll--table .MuiTableCell-head':
    {
      backgroundColor: 'var(--app-scroll-sheet-surface)',
      backdropFilter: 'var(--app-scroll-sheet-blur)',
      WebkitBackdropFilter: 'var(--app-scroll-sheet-blur)',
      boxShadow: '0 1px 0 var(--app-border-faint)',
    },
  '& .MuiTableCell-head, & .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader':
    {
      backgroundColor: 'var(--app-table-head-surface)',
    },
  '& .MuiDataGrid-cell': {
    borderColor: 'var(--app-border-subtle)',
  },
  '& .MuiAvatar-root.MuiAvatar-colorDefault': {
    background: 'var(--app-avatar-bg)',
    color: 'var(--app-avatar-color)',
    border: '1px solid var(--app-avatar-border)',
    boxShadow: 'var(--app-avatar-shadow)',
    backdropFilter: 'var(--app-avatar-blur)',
    WebkitBackdropFilter: 'var(--app-avatar-blur)',
    fontWeight: 500,
  },
  '& .MuiAvatar-root.MuiAvatar-colorDefault svg': {
    color: 'inherit',
  },
  '& .MuiTabs-root:not(.MuiTabs-vertical)': {
    borderBottomColor: 'var(--app-border-subtle)',
  },
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  '& .MuiListItemButton-root:not(.Mui-disabled), & .MuiTab-root:not(.Mui-disabled)':
    {
      transition:
        'background-color 160ms ease, background 160ms ease, box-shadow 160ms ease, border-color 160ms ease, color 160ms ease',
      borderRadius: '8px',
      position: 'relative',
    },
  '& .MuiListItemButton-root:not(.Mui-disabled):hover, & .MuiTab-root:not(.Mui-disabled):hover':
    {
      background: 'var(--app-menu-hover-bg)',
      boxShadow: 'var(--app-menu-hover-shadow)',
      color: 'var(--app-hover-color)',
    },
  '& .MuiListItemButton-root.Mui-selected, & .MuiListItemButton-root.Mui-selected:hover, & .MuiTab-root.Mui-selected, & .MuiTab-root.Mui-selected:hover':
    {
      background: 'var(--app-menu-active-bg)',
      boxShadow: 'var(--app-menu-active-shadow)',
      color: 'var(--app-menu-active-color)',
    },
  '& .MuiListItemButton-root.Mui-selected::after': {
    content: '""',
    position: 'absolute',
    left: 6,
    top: 10,
    bottom: 10,
    width: 2,
    borderRadius: 4,
    backgroundColor: 'var(--app-active-accent)',
  },
  '& .MuiTab-root.Mui-selected::after': {
    content: '""',
    position: 'absolute',
    left: 18,
    right: 18,
    bottom: 6,
    height: 2,
    borderRadius: 4,
    backgroundColor: 'var(--app-active-accent)',
  },
  '& .MuiListItemButton-root.Mui-selected .MuiListItemText-primary, & .MuiListItemButton-root.Mui-selected .MuiListItemText-secondary, & .MuiListItemButton-root.Mui-selected svg':
    {
      color: 'var(--app-menu-active-color)',
    },
  '& .MuiOutlinedInput-root, & .MuiInputBase-root.MuiOutlinedInput-root': {
    backgroundColor: 'var(--app-input-surface)',
  },
  '& .MuiButton-outlined, & .MuiButton-text': {
    backgroundColor: 'var(--app-button-surface)',
    borderColor: 'var(--app-button-border)',
    borderRadius: '8px',
    lineHeight: 1.35,
    alignItems: 'center',
  },
  '& .MuiButton-outlined .MuiButton-startIcon, & .MuiButton-text .MuiButton-startIcon':
    {
      display: 'inline-flex',
      alignItems: 'center',
      marginLeft: 0,
    },
  '& .MuiButton-outlined svg, & .MuiButton-text svg': {
    flexShrink: 0,
  },
  transform: modalOpened ? 'scale(0.98)' : 'scale(1)',

  transition: open
    ? theme.transitions.create(['margin', 'transform'], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      })
    : theme.transitions.create(['margin', 'transform'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
  marginLeft: !isMobile ? `${drawerWidth}px` : 0,
}))

const VerticalLayout = (props: LayoutProps) => {
  const { query } = useSearchQuery()
  const { children, scrollToTop } = props
  const [navState, setNavState] = React.useState(false)
  const { isMobile } = useResponsive()
  const modalOpened = Boolean(query.modal)
  const { initialize: initializeCommunicationService } = useSelectedAccounts(
    AccountsType.COMMUNICATION
  )
  const { initialize: initializeEmailService } = useSelectedAccounts(
    AccountsType.EMAIL
  )
  const initializeTwilio = useTwilioNewActions.initialize()

  React.useEffect(() => {
    initializeTwilio()
  }, [])

  const isMobileWebView = useAccountSelect.isMobileWebView()
  const windowOpenRightSideBar = useAssistantSelect.windowOpenRightSideBar()
  const setNavHandle = () => {
    setNavState((prev) => !prev)
  }

  React.useEffect(() => {
    if (!isMobileWebView) {
      initializeEmailService()
      initializeCommunicationService()
    }
  }, [isMobileWebView])

  return (
    <>
      <LayoutContext.Provider value={{ navState, setNavHandle }}>
        <Stack
          direction="row"
          sx={{
            height: '100%',
            width: '100%',
            paddingRight: windowOpenRightSideBar ? '375px' : 0,
          }}
        >
          <SearchAsyncModal />
          <AssinstantWindow />
          <Navigation />

          <Main open={navState} isMobile={isMobile} modalOpened={modalOpened}>
            {!isMobileWebView && (
              <Stack
                pt={{ sm: 3, xl: 6 }}
                sx={{
                  marginLeft: (theme) => ({
                    xs: theme.spacing(3),
                    md: theme.spacing(6),
                  }),
                  marginRight: (theme) => ({
                    xs: theme.spacing(3),
                    md: theme.spacing(6),
                  }),
                }}
              >
                <GlobalSearchComponent />
              </Stack>
            )}

            <Stack
              pt={{ xs: 5, xl: 6 }}
              flex={1}
              minHeight={0}
              direction="column"
              sx={{
                transition: 'padding .25s ease-in-out',
                overflow: 'hidden',
              }}
            >
              {children}
            </Stack>
          </Main>
        </Stack>
      </LayoutContext.Provider>

      {scrollToTop ? (
        scrollToTop(props)
      ) : (
        <ScrollToTop className="mui-fixed">
          <Fab color="primary" size="small" aria-label="scroll back to top">
            <Icon icon="tabler:arrow-up" />
          </Fab>
        </ScrollToTop>
      )}
    </>
  )
}

export default VerticalLayout
